/**
 * Card Component
 * =================
 * Reusable card container component.
 */

import { forwardRef } from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// CARD
// =============================================================================

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'elevated';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hoverable?: boolean;
  clickable?: boolean;
}

const variantStyles: Record<string, string> = {
  default: 'bg-white border border-gray-200',
  bordered: 'bg-white border-2 border-gray-300',
  elevated: 'bg-white shadow-md',
};

const paddingStyles: Record<string, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      padding = 'md',
      hoverable = false,
      clickable = false,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg',
          variantStyles[variant],
          paddingStyles[padding],
          hoverable && 'transition-shadow duration-150 hover:shadow-lg',
          clickable && 'cursor-pointer transition-all duration-150 hover:shadow-lg active:scale-[0.98]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// =============================================================================
// CARD HEADER
// =============================================================================

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  action?: React.ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, action, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between gap-4', className)}
        {...props}
      >
        <div className="space-y-1">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          )}
          {description && (
            <p className="text-sm text-gray-500">{description}</p>
          )}
          {children}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

// =============================================================================
// CARD BODY
// =============================================================================

export interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('mt-4', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

// =============================================================================
// CARD FOOTER
// =============================================================================

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  bordered?: boolean;
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, bordered = true, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'mt-4 flex items-center justify-end gap-3',
          bordered && 'border-t border-gray-200 pt-4',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

// =============================================================================
// STAT CARD
// =============================================================================

export interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
  className?: string;
}

export function StatCard({
  title,
  value,
  description,
  icon,
  trend,
  className,
}: StatCardProps) {
  return (
    <Card className={cn('flex items-start gap-4', className)}>
      {icon && (
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          {icon}
        </div>
      )}
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <div className="mt-1 flex items-baseline gap-2">
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {trend && (
            <span
              className={cn(
                'inline-flex items-center text-sm font-medium',
                trend.isPositive ? 'text-green-600' : 'text-red-600'
              )}
            >
              {trend.isPositive ? '↑' : '↓'} {Math.abs(trend.value)}%
              {trend.label && (
                <span className="ml-1 text-gray-500">{trend.label}</span>
              )}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        )}
      </div>
    </Card>
  );
}

// =============================================================================
// PATIENT CARD
// =============================================================================

export interface PatientCardProps {
  name: string;
  dateOfBirth?: string;
  chartNumber?: string;
  phone?: string;
  riskLevel?: 'low' | 'moderate' | 'high';
  lastVisit?: string;
  onClick?: () => void;
  className?: string;
}

export function PatientCard({
  name,
  dateOfBirth,
  chartNumber,
  phone,
  riskLevel,
  lastVisit,
  onClick,
  className,
}: PatientCardProps) {
  const riskColors = {
    low: 'bg-green-100 text-green-800',
    moderate: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-800',
  };

  return (
    <Card
      clickable={!!onClick}
      onClick={onClick}
      className={cn('relative', className)}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-gray-900">{name}</h3>
          {chartNumber && (
            <p className="text-sm text-gray-500">Chart #: {chartNumber}</p>
          )}
        </div>
        {riskLevel && (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 text-xs font-medium',
              riskColors[riskLevel]
            )}
          >
            {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Risk
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        {dateOfBirth && (
          <div>
            <span className="text-gray-500">DOB:</span>{' '}
            <span className="text-gray-900">{dateOfBirth}</span>
          </div>
        )}
        {phone && (
          <div>
            <span className="text-gray-500">Phone:</span>{' '}
            <span className="text-gray-900">{phone}</span>
          </div>
        )}
        {lastVisit && (
          <div className="col-span-2">
            <span className="text-gray-500">Last Visit:</span>{' '}
            <span className="text-gray-900">{lastVisit}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

export default Card;
